import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { Toaster } from 'sonner';
import { logError } from './lib/error-logger';
import { lazyRetry } from './lib/lazy-retry';
import { AuthProvider } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { AdminProtectedRoute } from './components/auth/AdminProtectedRoute';
import { AdminSuperOnlyRoute } from './components/auth/AdminSuperOnlyRoute';
import { ClientProtectedRoute } from './components/auth/ClientProtectedRoute';
import { RootRedirect } from './components/auth/RootRedirect';
import { AdminDefaultRedirect } from './components/auth/AdminDefaultRedirect';
import { LoadingSpinner } from '@looma/ui';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { isAdminDomain, redirectToDashboard } from './lib/domain';

/** Blocks public/staff routes on admin domain — redirects to dashboard domain */
function DashboardOnlyRoute({ children }: { children: React.ReactNode }) {
  if (isAdminDomain()) {
    redirectToDashboard(window.location.pathname);
    return null;
  }
  return <>{children}</>;
}

/** Redirects /:slug/accounting/sync → /:slug/accounting/journal (Sync POS folded into Journal) */
function RedirectAccountingSync() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/${slug}/accounting/journal`} replace />;
}

// Lazy load pages with retry on chunk load failure
const Login = lazy(lazyRetry(() => import('./pages/Login')));
const Unauthorized = lazy(lazyRetry(() => import('./pages/Unauthorized')));

// Admin Pages
const AdminDashboard = lazy(lazyRetry(() => import('./pages/admin/Dashboard')));
const AdminClients = lazy(lazyRetry(() => import('./pages/admin/Clients')));
const AdminServices = lazy(lazyRetry(() => import('./pages/admin/Services')));
const AdminIntelligence = lazy(lazyRetry(() => import('./pages/admin/Intelligence')));
const AdminBilling = lazy(lazyRetry(() => import('./pages/admin/Billing')));
const AdminBroadcast = lazy(lazyRetry(() => import('./pages/admin/Broadcast')));
const AdminLogs = lazy(lazyRetry(() => import('./pages/admin/Logs')));

// Client Pages
const ClientDashboard = lazy(lazyRetry(() => import('./pages/client/Dashboard')));
// VITRINE-ONLINE-HUB-01 — the « Vitrine & commandes en ligne » page replaces QR Menu.
const VitrineOnline = lazy(lazyRetry(() => import('./pages/client/VitrineOnline')));
const ClientPOS = lazy(lazyRetry(() => import('./pages/client/POS')));
const Settings = lazy(lazyRetry(() => import('./pages/Settings')));

// POS Sub-pages (Owner)
const CataloguePage = lazy(lazyRetry(() => import('./pages/client/pos/Catalogue')));
const TransactionsPage = lazy(lazyRetry(() => import('./pages/client/pos/Transactions')));
const ExpensesPage = lazy(lazyRetry(() => import('./pages/client/pos/Expenses')));
const ReconciliationPage = lazy(lazyRetry(() => import('./pages/client/pos/Reconciliation')));
const SuppliersPage = lazy(lazyRetry(() => import('./pages/client/pos/Suppliers')));
const CustomersPage = lazy(lazyRetry(() => import('./pages/client/pos/Customers')));
const ServicesPage = lazy(lazyRetry(() => import('./pages/client/pos/ShiftsPage')));
const ReportsPage = lazy(lazyRetry(() => import('./pages/client/pos/ReportsPage')));
const AuditLogPage = lazy(lazyRetry(() => import('./pages/client/pos/AuditLog')));

// Accounting Pages
const AccountingDashboard = lazy(lazyRetry(() => import('./pages/client/accounting/AccountingDashboard')));
const AccountingJournal = lazy(lazyRetry(() => import('./pages/client/accounting/Journal')));
const AccountingChartOfAccounts = lazy(lazyRetry(() => import('./pages/client/accounting/ChartOfAccounts')));
const AccountingGeneralLedger = lazy(lazyRetry(() => import('./pages/client/accounting/GeneralLedger')));
const AccountingTrialBalance = lazy(lazyRetry(() => import('./pages/client/accounting/TrialBalance')));
const AccountingBalanceSheet = lazy(lazyRetry(() => import('./pages/client/accounting/BalanceSheet')));
const AccountingIncomeStatement = lazy(lazyRetry(() => import('./pages/client/accounting/IncomeStatement')));
const AccountingTemplates = lazy(lazyRetry(() => import('./pages/client/accounting/Templates')));
const AccountingFixedAssets = lazy(lazyRetry(() => import('./pages/client/accounting/FixedAssets')));
const AccountingSettings = lazy(lazyRetry(() => import('./pages/client/accounting/Settings')));
const AccountingCashFlow = lazy(lazyRetry(() => import('./pages/client/accounting/CashFlow')));
const AccountingBankReconciliation = lazy(lazyRetry(() => import('./pages/client/accounting/BankReconciliation')));
const AccountingARaging = lazy(lazyRetry(() => import('./pages/client/accounting/ARaging')));
const AccountingYearEndClosing = lazy(lazyRetry(() => import('./pages/client/accounting/YearEndClosing')));
const AccountingCategoryMappings = lazy(lazyRetry(() => import('./pages/client/accounting/CategoryMappings')));

// Staff POS Pages (Public, code-based auth)
const StaffLoginPage = lazy(lazyRetry(() => import('./pages/client/pos-staff/Login')));
const StaffTransactionPage = lazy(lazyRetry(() => import('./pages/client/pos-staff/Transaction')));

// Google Drive OAuth Callback
const GoogleDriveCallback = lazy(lazyRetry(() => import('./pages/GoogleDriveCallback')));

// Public Pages
const PublicMenu = lazy(lazyRetry(() => import('./pages/public/PublicMenu')));
const Commander = lazy(lazyRetry(() => import('./pages/public/Commander')));
const OrderStatus = lazy(lazyRetry(() => import('./pages/public/OrderStatus')));
const NotreHistoire = lazy(lazyRetry(() => import('./pages/public/NotreHistoire')));

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      logError({ type: 'query', error, metadata: { queryKey: query.queryKey } });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      logError({ type: 'mutation', error, metadata: { mutationKey: mutation.options.mutationKey } });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function RedirectToCatalogue() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/${slug}/pos/catalogue`} replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <OrganizationProvider>
            <ErrorBoundary>
              <Suspense fallback={
              <div className="min-h-dvh flex items-center justify-center">
                <LoadingSpinner />
              </div>
            }>
              <Routes>
                {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Admin Routes - MUST come before dynamic :slug routes */}
            <Route
              path="/admin/dashboard"
              element={
                <AdminSuperOnlyRoute>
                  <AdminDashboard />
                </AdminSuperOnlyRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <AdminProtectedRoute>
                  <AdminClients />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/services"
              element={
                <AdminSuperOnlyRoute>
                  <AdminServices />
                </AdminSuperOnlyRoute>
              }
            />
            <Route
              path="/admin/intelligence"
              element={
                <AdminProtectedRoute>
                  <AdminIntelligence />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/billing"
              element={
                <AdminSuperOnlyRoute>
                  <AdminBilling />
                </AdminSuperOnlyRoute>
              }
            />
            <Route
              path="/admin/broadcast"
              element={
                <AdminSuperOnlyRoute>
                  <AdminBroadcast />
                </AdminSuperOnlyRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <AdminSuperOnlyRoute>
                  <AdminLogs />
                </AdminSuperOnlyRoute>
              }
            />
            <Route path="/admin" element={<AdminDefaultRedirect />} />

            {/* Google Drive OAuth Callback - before dynamic :slug routes */}
            <Route path="/auth/google-drive-callback" element={<DashboardOnlyRoute><GoogleDriveCallback /></DashboardOnlyRoute>} />

            {/* Public Routes - No auth required (blocked on admin domain) */}
            <Route path="/:slug/menu" element={<DashboardOnlyRoute><PublicMenu /></DashboardOnlyRoute>} />
            <Route path="/:slug/commander" element={<DashboardOnlyRoute><Commander /></DashboardOnlyRoute>} />
            <Route path="/:slug/order-status/:draftId" element={<DashboardOnlyRoute><OrderStatus /></DashboardOnlyRoute>} />
            <Route path="/:slug/notre-histoire" element={<DashboardOnlyRoute><NotreHistoire /></DashboardOnlyRoute>} />

            {/* Staff POS Routes - Public, code-based auth (blocked on admin domain) */}
            <Route path="/:slug/pos-staff" element={<DashboardOnlyRoute><StaffLoginPage /></DashboardOnlyRoute>} />
            <Route path="/:slug/pos-staff/sale" element={<DashboardOnlyRoute><StaffTransactionPage /></DashboardOnlyRoute>} />

            {/* Client Routes - Dynamic slug-based routing */}
            <Route
              path="/:slug/dashboard"
              element={
                <ClientProtectedRoute>
                  <ClientDashboard />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/qr-menu"
              element={
                <ClientProtectedRoute>
                  <VitrineOnline />
                </ClientProtectedRoute>
              }
            />
            {/* VITRINE-HUB-SUBNAV-01 — each section has its own URL; /:slug/qr-menu opens Commandes. */}
            <Route
              path="/:slug/qr-menu/:section"
              element={
                <ClientProtectedRoute>
                  <VitrineOnline />
                </ClientProtectedRoute>
              }
            />
            {/* VITRINE-ONLINE-HUB-01 — friendly alias for the same page (opens Commandes). */}
            <Route
              path="/:slug/vitrine"
              element={
                <ClientProtectedRoute>
                  <VitrineOnline />
                </ClientProtectedRoute>
              }
            />

            {/* POS Routes - Authenticated owner/manager access */}
            <Route
              path="/:slug/pos"
              element={
                <ClientProtectedRoute>
                  <ClientPOS />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/pos/catalogue"
              element={
                <ClientProtectedRoute>
                  <CataloguePage />
                </ClientProtectedRoute>
              }
            />
            {/* Backward-compatible redirects */}
            <Route path="/:slug/pos/menu-items" element={<RedirectToCatalogue />} />
            <Route path="/:slug/pos/inventory" element={<RedirectToCatalogue />} />
            <Route path="/:slug/pos/ingredient-board" element={<RedirectToCatalogue />} />
            <Route
              path="/:slug/pos/transactions"
              element={
                <ClientProtectedRoute>
                  <TransactionsPage />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/pos/expenses"
              element={
                <ClientProtectedRoute>
                  <ExpensesPage />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/pos/suppliers"
              element={
                <ClientProtectedRoute>
                  <SuppliersPage />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/pos/customers"
              element={
                <ClientProtectedRoute>
                  <CustomersPage />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/pos/reconciliation"
              element={
                <ClientProtectedRoute>
                  <ReconciliationPage />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/pos/shifts"
              element={
                <ClientProtectedRoute>
                  <ServicesPage />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/pos/reports"
              element={
                <ClientProtectedRoute>
                  <ReportsPage />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/pos/audit-log"
              element={
                <ClientProtectedRoute>
                  <AuditLogPage />
                </ClientProtectedRoute>
              }
            />

            {/* Accounting Routes */}
            <Route
              path="/:slug/accounting"
              element={
                <ClientProtectedRoute>
                  <AccountingDashboard />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/journal"
              element={
                <ClientProtectedRoute>
                  <AccountingJournal />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/accounts"
              element={
                <ClientProtectedRoute>
                  <AccountingChartOfAccounts />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/settings"
              element={
                <ClientProtectedRoute>
                  <AccountingSettings />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/general-ledger"
              element={
                <ClientProtectedRoute>
                  <AccountingGeneralLedger />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/trial-balance"
              element={
                <ClientProtectedRoute>
                  <AccountingTrialBalance />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/balance-sheet"
              element={
                <ClientProtectedRoute>
                  <AccountingBalanceSheet />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/income-statement"
              element={
                <ClientProtectedRoute>
                  <AccountingIncomeStatement />
                </ClientProtectedRoute>
              }
            />
            {/* Legacy /accounting/sync redirects to Journal where inline sync now lives. */}
            <Route path="/:slug/accounting/sync" element={<RedirectAccountingSync />} />
            <Route
              path="/:slug/accounting/templates"
              element={
                <ClientProtectedRoute>
                  <AccountingTemplates />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/mappings"
              element={
                <ClientProtectedRoute>
                  <AccountingCategoryMappings />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/assets"
              element={
                <ClientProtectedRoute>
                  <AccountingFixedAssets />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/cash-flow"
              element={
                <ClientProtectedRoute>
                  <AccountingCashFlow />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/bank-reconciliation"
              element={
                <ClientProtectedRoute>
                  <AccountingBankReconciliation />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/ar-aging"
              element={
                <ClientProtectedRoute>
                  <AccountingARaging />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/:slug/accounting/year-end"
              element={
                <ClientProtectedRoute>
                  <AccountingYearEndClosing />
                </ClientProtectedRoute>
              }
            />

            {/* Settings */}
            <Route
              path="/:slug/settings"
              element={
                <ClientProtectedRoute>
                  <Settings />
                </ClientProtectedRoute>
              }
            />

            {/* Default slug redirect */}
            <Route
              path="/:slug"
              element={
                <ClientProtectedRoute>
                  <Navigate to="pos" replace />
                </ClientProtectedRoute>
              }
            />

            {/* Smart redirect based on user role */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </Suspense>
        <Toaster position="bottom-right" />
      </ErrorBoundary>
    </OrganizationProvider>
    </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);
}

export default App;
